/**
 * @file SeoVerificationCard.tsx
 * @description «Сайт → SEO → Подтверждение сайта» (phase19.16 Р1 → Р3в,
 * v0.19.64; SEO1, SB145): списки кодов Яндекс Вебмастера и Google Search
 * Console, до 5 на поисковик (владелец, агентство, сотрудник). Можно вставить
 * тег целиком — на сайт уходит только код. Сохраняется сразу; удаление кода —
 * с предупреждением «кабинет потеряет подтверждение при следующей проверке».
 *
 * Поисковики — реестром `ENGINES`: новый — строка, а не ветка.
 */

import type { SeoVerificationEngine } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { SectionCard } from "../../ui/SectionCard";
import { SeoCardActions } from "./SeoCardActions";
import { SeoFieldMark } from "./SeoFieldMark";
import type { SeoCardProps } from "./types";
import { VerificationCodeList } from "./VerificationCodeList";
import { useSeoCardForm } from "../../../hooks/site-seo/useSeoCardForm";

type Key = "yandexVerification" | "googleVerification";

/** Поле формы → список кодов в ответе сервера. */
const SERVER_LIST: Readonly<Record<Key, SeoVerificationEngine>> = { yandexVerification: "yandex", googleVerification: "google" };

const ENGINES: ReadonlyArray<{ key: Key; engine: SeoVerificationEngine; label: string; hint: string }> = [
  {
    key: "yandexVerification",
    engine: "yandex",
    label: "Яндекс Вебмастер",
    hint: "Вебмастер → Добавить сайт → Мета-тег: вставьте тег целиком или код из content.",
  },
  {
    key: "googleVerification",
    engine: "google",
    label: "Google Search Console",
    hint: "Search Console → Другие способы → HTML-тег: вставьте тег целиком или код.",
  },
];

const KEYS: readonly Key[] = ["yandexVerification", "googleVerification"];

export function SeoVerificationCard({ api, settings, onHistory, readOnly }: SeoCardProps) {
  const form = useSeoCardForm(api, KEYS);
  const [invalid, setInvalid] = useState<Partial<Record<Key, boolean>>>({});
  // Новый ответ сервера или «Отменить» — строки ввода заново из сохранённых кодов.
  const [round, setRound] = useState(0);
  // Строки пересоздаются (новая версия настроек) — прежние неразборчивые строки исчезли.
  useEffect(() => setInvalid({}), [settings.version]);
  const removed = KEYS.some((key) => settings.verification[SERVER_LIST[key]].some((code) => !form.values[key].includes(code)));
  const blockedReason = Object.values(invalid).some(Boolean) ? "Исправьте неразборчивый код или удалите строку." : null;
  const fieldError = form.fieldError?.text ?? null;

  return (
    <SectionCard title="Подтверждение сайта">
      <p className="type-caption text-on-surface-variant">
        Нужно один раз, чтобы видеть статистику сайта в поисковике. На сайт попадёт только код — сразу после сохранения.
      </p>
      {ENGINES.map((e) => (
        <div key={`${e.key}:${round}:${settings.version}`} className="flex min-w-0 flex-col gap-1">
          <VerificationCodeList
            engine={e.engine}
            label={e.label}
            hint={e.hint}
            initial={form.values[e.key]}
            disabled={readOnly}
            onChange={(codes, bad) => {
              form.set(e.key, codes);
              setInvalid((prev) => ({ ...prev, [e.key]: bad }));
            }}
          />
          <SeoFieldMark field={e.key} settings={settings} onHistory={onHistory} readOnly={readOnly} />
        </div>
      ))}
      {removed && form.dirty ? (
        <p className="type-caption text-warning">Удалённый код: кабинет поисковика потеряет подтверждение при следующей проверке.</p>
      ) : null}
      {fieldError ? <p className="type-caption text-destructive">{fieldError}</p> : null}
      <SeoCardActions
        form={{
          ...form,
          reset: () => {
            form.reset();
            setInvalid({});
            setRound((n) => n + 1);
          },
        }}
        readOnly={readOnly}
        blockedReason={blockedReason}
      />
    </SectionCard>
  );
}
